import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import raceLaunchVideo from '../../assets/ziaracelanding.mp4';

export const RACE_LAUNCH_SPLASH_FALLBACK_MS = 6200;
export const RACE_LAUNCH_SPLASH_SKIP_KEY = 'vamosar_skip_race_splash';

function readSkipSplashPref(): boolean {
  try {
    return localStorage.getItem(RACE_LAUNCH_SPLASH_SKIP_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSkipSplashPref(skip: boolean) {
  try {
    if (skip) localStorage.setItem(RACE_LAUNCH_SPLASH_SKIP_KEY, '1');
    else localStorage.removeItem(RACE_LAUNCH_SPLASH_SKIP_KEY);
  } catch {
    /* private mode */
  }
}

interface RaceLaunchSplashProps {
  onComplete: () => void;
  onPreloadRaceApp?: () => void;
}

export function RaceLaunchSplash({ onComplete, onPreloadRaceApp }: RaceLaunchSplashProps) {
  const completedRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useLayoutEffect(() => {
    onPreloadRaceApp?.();
    const video = videoRef.current;
    if (video) {
      video.muted = false;
      video.volume = 1;
      try {
        const playRequest = video.play();
        void playRequest?.catch(() => {});
      } catch {
        // Browser autoplay policy may still block unmuted media in some webviews.
      }
    }

    const timeoutId = window.setTimeout(() => {
      if (completedRef.current) return;
      completedRef.current = true;
      onComplete();
    }, RACE_LAUNCH_SPLASH_FALLBACK_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [onComplete, onPreloadRaceApp]);

  const [skipNextTime, setSkipNextTime] = useState(readSkipSplashPref);

  const finishSplash = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  };

  const toggleSkip = () => {
    setSkipNextTime((current) => {
      const next = !current;
      writeSkipSplashPref(next);
      return next;
    });
  };

  return (
    <Box
      data-testid="race-launch-splash"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        width: '100vw',
        height: '100dvh',
        overflow: 'hidden',
        background: '#020617',
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        data-testid="race-launch-splash-video"
        src={raceLaunchVideo}
        autoPlay
        muted={false}
        playsInline
        preload="auto"
        onEnded={finishSplash}
        onError={finishSplash}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          background: '#020617',
        }}
      />

      {/* Skip controls: skip now, and a "don't show next time" toggle. */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 22px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          px: 2,
        }}
      >
        <Box
          role="checkbox"
          aria-checked={skipNextTime}
          tabIndex={0}
          data-testid="race-splash-skip-next"
          onClick={toggleSkip}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleSkip();
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.9,
            borderRadius: 999,
            cursor: 'pointer',
            background: 'rgba(2,6,23,0.55)',
            border: '1px solid rgba(34,211,238,0.4)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: 0.8,
              border: `2px solid ${skipNextTime ? '#22d3ee' : 'rgba(226,232,240,0.7)'}`,
              background: skipNextTime ? '#22d3ee' : 'transparent',
              color: '#020617',
              fontWeight: 950,
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {skipNextTime ? '✓' : ''}
          </Box>
          <Typography sx={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 800 }}>
            Don't show this intro next time
          </Typography>
        </Box>
        <Button
          data-testid="race-splash-skip-now"
          onClick={finishSplash}
          sx={{
            minHeight: 40,
            px: 3,
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 900,
            color: '#020617',
            background: 'linear-gradient(135deg, #22d3ee, #3b82f6)',
            '&:hover': { background: 'linear-gradient(135deg, #67e8f9, #60a5fa)' },
          }}
        >
          Skip ▶
        </Button>
      </Box>
    </Box>
  );
}

interface RaceRouteSplashGateProps {
  children: ReactNode;
  onPreloadRaceApp?: () => void;
}

export function RaceRouteSplashGate({ children, onPreloadRaceApp }: RaceRouteSplashGateProps) {
  // Honor the "don't show next time" preference — skip the intro entirely.
  const [complete, setComplete] = useState(readSkipSplashPref);

  if (!complete) {
    return (
      <RaceLaunchSplash
        onComplete={() => setComplete(true)}
        onPreloadRaceApp={onPreloadRaceApp}
      />
    );
  }

  return <>{children}</>;
}

export default RaceLaunchSplash;
